import type { IBreadcrumbItem } from '@fluentui/react';
import { useHaicPageTitle } from '@h2oai/ui-kit';
import type { History } from 'history';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import semver from 'semver';

import { HeaderPure } from '../../components/Header/Header';
import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { DeploymentFixed } from '../../mlops/api';
import { DeploymentStatus_DeploymentState } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_status_pb';
import { AuthenticationPassphrase_HashAlgorithm } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/security_pb';
import {
  useQueryDeployment,
  useQueryDeploymentMetadata,
  useQueryDeploymentStatus,
  useQueryMonitoringConfig,
} from '../../mlops/mlops.deployer.hooks';
import { getSecurityKey, type SecurityKey } from '../../mlops/utils';
import { useEnv } from '../../utils/hooks';
import { RoutePaths } from '../Routes';
import { WORKSPACE_DEPLOYMENT_ID_SEPARATOR } from './constants';
import { DeploymentDetailContent, DeploymentTab } from './DeploymentDetail';
import { getDeploymentType, IDeploymentTabDetailProps } from './DeploymentTabDetail';
import { IDeploymentTabEndpointsProps } from './DeploymentTabEndpoints';
import { IDeploymentTabMonitoringProps } from './DeploymentTabMonitoring';
import { IDeploymentTabScoringProps } from './DeploymentTabScoring';
import PageWrapper from './PageWrapper';

const SEARCH_QUERY_PARAM_TAB_ID = 'tabId';

const usePivotTab = (history: History) => {
  const location = useLocation();
  const selectedTab = React.useMemo<DeploymentTab>(() => {
    const urlParams = new URLSearchParams(location.search);
    return Number(urlParams.get(SEARCH_QUERY_PARAM_TAB_ID)) || DeploymentTab.DETAILS;
  }, [location.search]);
  const onTabClick = React.useCallback(
    (dt: DeploymentTab) => {
      const urlParams = new URLSearchParams(location.search);
      if (dt === DeploymentTab.DETAILS) {
        urlParams.delete(SEARCH_QUERY_PARAM_TAB_ID);
      } else {
        urlParams.set(SEARCH_QUERY_PARAM_TAB_ID, String(dt));
      }
      // Update the URL without reloading the page
      history.replace({ ...location, search: urlParams.toString() });
    },
    [location]
  );
  return { selectedTab, onTabClick };
};

type DeploymentEnhanced = DeploymentFixed & {
  defaultSelectedSecurityOptionKey?: SecurityKey;
};

export const AdminDeploymentDetail = () => {
  const history = useHistory();
  const env = useEnv();
  const params = useParams<{ workspace_deployment_id: string }>();
  const [workspaceId, deploymentId] = params.workspace_deployment_id.split(WORKSPACE_DEPLOYMENT_ID_SEPARATOR, 2);
  const deploymentQuery = useQueryDeployment(
    { deploymentId, workspaceId },
    {
      select: (data) => {
        const securityKey = getSecurityKey(data);
        const deploymentEnhanced: DeploymentEnhanced = {
          ...(data as DeploymentFixed),
          defaultSelectedSecurityOptionKey: securityKey,
        };
        return deploymentEnhanced;
      },
    }
  );
  const deployment = deploymentQuery.data;
  const deploymentStatusQuery = useQueryDeploymentStatus({ deploymentId, workspaceId });
  const deploymentStatus = deploymentStatusQuery.data;
  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');
  const updateDeleteAllowed = isMlopsApiV1_0_x
    ? deploymentStatus?.state === DeploymentStatus_DeploymentState.HEALTHY ||
      deploymentStatus?.state === DeploymentStatus_DeploymentState.FAILED ||
      deploymentStatus?.state === DeploymentStatus_DeploymentState.UNHEALTHY ||
      deploymentStatus?.state === DeploymentStatus_DeploymentState.STOPPED
    : deploymentStatus?.updatesAllowed ?? false;
  const isNotHealthy = deploymentStatus?.state !== DeploymentStatus_DeploymentState.HEALTHY;
  const monitoringQuery = useQueryMonitoringConfig();
  const monitoringConfig = monitoringQuery.data?.monitoringConfig;
  const monitoringGloballyDisabled = !monitoringConfig?.enabled;
  const deploymentMetadataQuery = useQueryDeploymentMetadata({ deploymentId, workspaceId });
  const deploymentMetadata = deploymentMetadataQuery.data;
  const scorerEndpoint = deploymentMetadata?.scoringInterface?.score?.uri || ``;
  const detailTabProps: IDeploymentTabDetailProps = {
    deployment,
    deploymentStatus,
    defaultSelectedSecurityOptionKey: deployment?.defaultSelectedSecurityOptionKey,
    isNew: false,
    updateDeleteAllowed,
    isMonitoringEnabledGlobally: !monitoringGloballyDisabled,
    workspaceId,
  };
  const endpointsTabProps: IDeploymentTabEndpointsProps = {
    isNew: false,
    scorerEndpoint,
    deploymentId: deployment?.name?.split('/').pop() || '',
    workspaceId,
  };
  const monitoringTabProps: IDeploymentTabMonitoringProps | undefined =
    monitoringGloballyDisabled || !deployment?.monitoringOptions?.enabled
      ? undefined
      : {
          deploymentId,
          /*
           * For deployments with 2 models, the first model's experiment ID is used.
           * Note, the same logic is used when setting monitoring options during deployment creation.
           * If the deployment creation logic is changed, this needs to be updated as well.
           * Alternatively, we can obtain the experiment ID from the deployment monitoring metadata.
           */
          experimentId:
            deployment?.single?.deploymentComposition?.experimentId ||
            deployment?.shadow?.primaryElement?.deploymentComposition?.experimentId ||
            deployment?.split?.splitElements?.[0]?.deploymentComposition?.experimentId ||
            '',
          deploymentType: getDeploymentType(deployment),
          secondaryExperimentId: deployment?.single?.deploymentComposition?.experimentId
            ? undefined
            : deployment?.shadow?.secondaryElement?.deploymentComposition?.experimentId ||
              deployment?.split?.splitElements?.[1]?.deploymentComposition?.experimentId,
          frontendUri: monitoringConfig?.frontendUri,
          columnNames: deployment?.monitoringOptions?.columns?.map((col) => col.column),
          deploymentCreationTime: deployment?.createTime ? new Date(deployment?.createTime) : undefined,
          workspaceId,
        };
  const scoringTabProps: IDeploymentTabScoringProps = {
    defaultSelectedSecurityOptionKey: deployment?.defaultSelectedSecurityOptionKey,
    scorerEndpoint,
    sampleRequestUrl: deploymentMetadata?.scoringInterface?.sampleRequest?.uri || '',
    plainTextPassphrase:
      (deployment?.security &&
        `passphrase` in deployment.security &&
        deployment.security.passphrase!.hashAlgorithm === AuthenticationPassphrase_HashAlgorithm.PLAINTEXT &&
        deployment.security!.passphrase!.passphrase) ||
      '',
    isNew: false,
    hasNoReplicas:
      deployment?.single?.kubernetesResourceSpec?.replicas === 0 ||
      deployment?.shadow?.primaryElement?.kubernetesResourceSpec?.replicas === 0 ||
      deployment?.shadow?.secondaryElement?.kubernetesResourceSpec?.replicas === 0 ||
      deployment?.split?.splitElements?.[0]?.kubernetesResourceSpec?.replicas === 0 ||
      deployment?.split?.splitElements?.[1]?.kubernetesResourceSpec?.replicas === 0,
    workspaceId,
  };
  const { onTabClick, selectedTab } = usePivotTab(history);
  const breadcrumbs = React.useMemo<IBreadcrumbItem[]>(
    () => [
      {
        key: `home`,
        text: `home`,
        onClick: () => history.push('/'),
      },
      {
        key: `admin-deployments`,
        text: `Admin Deployments`,
        onClick: () => history.push(RoutePaths.MLOPS_ADMIN_DEPLOYMENTS),
      },
      {
        key: `deployment`,
        text: deployment?.displayName || deploymentId,
        isCurrentItem: true,
      },
    ],
    [deployment?.displayName, history]
  );

  useHaicPageTitle(`${deployment?.displayName || 'Deployment Detail'} | MLOps Admin`, env?.cloudInstanceName);

  return (
    <PageWrapper>
      <HeaderPure pageTitle="Deployment detail" items={breadcrumbs} />
      <DeploymentDetailContent
        detailTabProps={detailTabProps}
        endpointsTabProps={endpointsTabProps}
        monitoringTabProps={monitoringTabProps}
        onTabClick={onTabClick}
        scoringTabProps={scoringTabProps}
        selectedTab={selectedTab}
        statusUnhealthy={isNotHealthy}
        workspaceId={workspaceId}
      />
    </PageWrapper>
  );
};
